import React, { useState, useEffect, useContext } from 'react'
import { LeagueContractsContext } from '../../Context'
import { DisclaimerText, Inner, TableCon } from './styles'
import { Box } from '../../Components/Layout'
import { PlayerWithContract } from '../../Api'
import { Spinner } from '../../Components/Icons'
import { colors } from '../../Components/consts'
import { TeamSelect } from './Selectors/Team/TeamSelect'
import { PositionSelect } from './Selectors/Position/PositionSelect'
import { EndYearSelect } from './Selectors/EndYear/EndYearSelect'
import { createUrl } from '../../Routing/urls'
import { Con } from '../../Components/Layout/Containers/ConStyles'
import { AdUnit } from '../../Components/Ads/AdUnit'
import { CONTRACT_AD_INSERT_NUMBER, shouldShowAd } from '../../Utils'
import { Lk } from '../../Components/Links/Lk'
import { TableHeader } from '../../Components/Tables/TableHeader'
import { ResetBtn } from '../../Components/Buttons/styles'

type SelectorsType = {
  team: string
  position: string
  endYear: string
}

export const ContractsTable: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { players, loading } = useContext(LeagueContractsContext)!
  const [playerList, setPlayerList] = useState<PlayerWithContract[]>(players)
  const [selectors, setSelectors] = useState<SelectorsType>({
    team: 'All Teams',
    position: 'All',
    endYear: 'All',
  })

  useEffect(() => {
    if (players.length > 0) {
      filterPlayers()
    }
  }, [players, selectors])

  const onChangeSelector = (selectorItem: string, value: string) => {
    setSelectors({
      ...selectors,
      [selectorItem]: value,
    })
  }

  const filterPlayers = () => {
    const filtered = players.filter(
      (t) =>
        (selectors.team === 'All Teams'
          ? t.currentTeam !== null
          : t.currentTeam?.fullName &&
            t.currentTeam.fullName.toLowerCase() === selectors.team.toLowerCase()) &&
        (selectors.endYear === 'All'
          ? t.currentTeam !== null
          : (t.contractStatus && t.contractStatus) === selectors.endYear) &&
        (selectors.position === 'All'
          ? t.currentTeam !== null
          : (t.currentTeam?.position && t.currentTeam.position) === selectors.position)
    )
    setPlayerList(filtered)
  }

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1
    }
    if (b[orderBy] > a[orderBy]) {
      return 1
    }
    return 0
  }

  type Order = 'asc' | 'desc'

  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
    if (orderBy === 'lastName') {
      return order === 'asc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy)
    }
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy)
  }

  function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
    const filtered: any[] = players.filter(
      (t) =>
        (selectors.team === 'All Teams'
          ? t.currentTeam !== null
          : t.currentTeam.fullName.toLowerCase() === selectors.team.toLowerCase()) &&
        (selectors.endYear === 'All'
          ? t.currentTeam !== null
          : (t.contractStatus && t.contractStatus) === selectors.endYear) &&
        (selectors.position === 'All'
          ? t.currentTeam !== null
          : (t.currentTeam?.position && t.currentTeam.position) === selectors.position)
    )
    const stabilizedThis = filtered.map((el, index) => [el, index] as [T, number])
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0])
      if (order !== 0) return order
      return a[1] - b[1]
    })
    return stabilizedThis.map((el) => el[0])
  }

  const clearFilters = () => {
    setSelectors({
      team: 'All Teams',
      position: 'All',
      endYear: 'All',
    })
    setPlayerList(players)
  }

  return (
    <Box noMargin padding={0}>
      <Inner>
        <Con noMargin noPadding justify="center">
          {loading ? (
            <>
              <Spinner fill={colors.yellow} width={30} height={30} />
              Loading...
            </>
          ) : (
            <TableCon>
              <div className="selectorCon">
                <TeamSelect current={selectors.team} onChangeTeam={onChangeSelector} />
                <EndYearSelect
                  current={selectors.endYear}
                  onChangeYear={onChangeSelector}
                />
                <PositionSelect
                  current={selectors.position}
                  onChangePosition={onChangeSelector}
                />
                <ResetBtn onClick={clearFilters}>Reset</ResetBtn>
              </div>
              {!loading && playerList.length > 0 && (
                <>
                  <div className="contractsCon">
                    <table className="table">
                      <thead>
                        <tr>
                          <TableHeader ariaLabel="Player">Player</TableHeader>
                          <TableHeader ariaLabel="Team">Team</TableHeader>
                          <TableHeader ariaLabel="Position">Position</TableHeader>
                          <TableHeader ariaLabel="Contract">Contract</TableHeader>
                        </tr>
                      </thead>

                      <tbody>
                        <tr>
                          <td colSpan={4}>
                            <DisclaimerText>
                              *Year listed indicates final year of contract term.
                            </DisclaimerText>
                          </td>
                        </tr>
                        {stableSort(
                          playerList,
                          getComparator('asc', 'contractStatus')
                        ).map((row, idx) => (
                          <React.Fragment key={`${row.officialId}-${row.slug}`}>
                            <tr>
                              <td>
                                <Lk
                                  link={createUrl('player', {
                                    firstName: row.firstName,
                                    lastName: row.lastName,
                                    slugName: row.slug,
                                    tab: 'profile',
                                  })}
                                  alt={`${row.firstName} ${row.lastName} profile`}
                                >
                                  {row.firstName} {row.lastName}{' '}
                                  {row.currentTeam.jerseyNum}
                                </Lk>
                              </td>
                              <td>
                                <Con noMargin noPadding align="center">
                                  <span>{row.currentTeam.fullName}</span>
                                </Con>
                              </td>
                              <td>{row.currentTeam.position}</td>
                              <td>{row.contractStatus}</td>
                            </tr>
                            {shouldShowAd(idx, CONTRACT_AD_INSERT_NUMBER) && (
                              <tr>
                                <td colSpan={4} align="center">
                                  <AdUnit
                                    inContent={true}
                                    bannerType="bannerInContent"
                                    aria-label="League Contracts table ad"
                                  />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </TableCon>
          )}
        </Con>
      </Inner>
    </Box>
  )
}
